import * as React from "react";
import { Suspense } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Route, RouteComponentProps, Switch } from "react-router";
import { Location } from "history";
import { withRouter } from "react-router-dom";
import { WSConnectionStatus } from "src/app/data/common/interfaces/WSConnectionStatus";
import AuthStatus from "src/app/redux/modules/auth/interfaces/AuthStatus";
import selectors from "src/app/redux/selectors";
import actions from "src/app/redux/store/actions";
import { IAppState } from "src/app/redux/store/reducers";
import { getRoutesConfig } from "src/routing";
import styles from "./App.css";
import UseRoleLink, { IUseRoleLinkParams } from "src/hooks/useRoleLink";
import LoadingOverlay from "src/components/LoadingOverlay/LoadingOverlay";
import { resolveSsoCrossAppTarget } from "src/layouts/common/SsoCheck/crossAppRedirect";
import ErrorBoundary from "src/components/ErrorBoundary/ErrorBoundary";
import { hot } from "react-hot-loader/root";
import ROUTE_PATHS from "src/routing/paths";
import getUrlParamValue, { updateUrlParamsValue } from "src/app/data/common/utils/queryParamsAccessor";
import { isEmbeddedIframe } from "src/app/data/common/utils/isEmbeddedIframe";
import { IExternalLoginData, PARAM_LOGIN_EXTERNAL } from "src/app/redux/modules/auth/interfaces/IAuthData";
import SsoCheck from "src/layouts/common/SsoCheck";
import Cookies from "js-cookie";
import LoadChunk from "src/app/services/LoadChunk/LoadChunk";
import moment from "moment-timezone";
import "src/i18n/momentLocaleIndex";
import { momentUpdateLocale } from "src/i18n/momentUpdateLocale";
import { getRoutesPath } from "src/layouts/common/LearnerOnboarding/LearnerRoutes";
import { LEARNER_ROLES } from "src/app/data/common/roles";
import _has from "lodash/has";
import {
  getCommonProjectConfig,
  checkVirtualSimRedirect,
} from "src/layouts/common/NavigationBar/utils/navigationBarUtils";
import { completedSessionsQuery } from "src/hooks/useLearnerDashboard";
import { ISessionForList } from "src/app/data/session/interfaces/ISession";

const SuccessPageRoute = LoadChunk(() => import("src/layouts/common/Static/SuccessPageRoute"));
const ErrorPageRoute = LoadChunk(() => import("src/layouts/common/Static/ErrorPageRoute"));
const ErrorModal = LoadChunk(() => import("src/components/ErrorModal"));
const CookiePopupContainer = LoadChunk(() => import("src/components/CookiePopup/CookiePopup"));
const RefreshSessionTimeoutModal = LoadChunk(() => import("src/components/RefreshSessionTimeoutModal"));
const EmailVerificationPage = LoadChunk(() => import("src/components/EmailVerificationPage/EmailVerificationPage"));

const LoginLoader = LoadChunk(() => import("src/layouts/unauthorized/Login/LoginLoader/LoginLoader"));

const AuthorizedRoot = LoadChunk(() => import("src/layouts/common/Root"));
const UnauthorizedRoot = LoadChunk(() => import("src/layouts/unauthorized/Root"));

const connector = connect(
  (state: IAppState) => ({
    profileLoaded: selectors.profile.isUserProfileLoaded(state),
    status: selectors.auth.getAuthStatus(state),
    userRole: selectors.profile.getCurrentUserRole(state),
    userProfile: selectors.profile.getUserProfile(state),
    wsConnectionStatus: selectors.ws.getConnectionStatus(state),
    language: selectors.profile.getUserLanguage(state),
    clientConfig: selectors.clientsConfig.getClientConfig(state),
    isUserOnboarded: selectors.profile.isUserOnboarded(state),
    companyConfig: selectors.companyConfig.getCompanyConfig(state),
    projects: selectors.projects.getProjectsForClientList(state),
    clientId: selectors.profile.getClientId(state),
  }),
  {
    authenticationCheck: actions.auth.authenticationCheck,
    initWebSocketConnection: actions.ws.initWebSocketConnection,
    logOut: actions.auth.logOut,
    switchRole: actions.auth.switchRole,
    saveToken: actions.auth.saveToken,
    getToken: actions.auth.getToken,
    refreshClientsList: actions.projects.refreshClientsList,
    fetchSessionListResponse: actions.session.fetchSessionListResponse,
    setScormRedirectUrl: actions.layout.setScormRedirectUrl,
  }
);

const HOST = "mursion.com";
const isDevelopment = (): boolean => window.location.hostname === "localhost" || process.env.NODE_ENV === "development";

type PropsFromRedux = ConnectedProps<typeof connector>;

interface IAppContainerState {
  showIntro: boolean;
  loaded: boolean;
  authFailedPath?: Location;
  redirectUrl?: string;
}

type Props = RouteComponentProps<any> & PropsFromRedux & { ldClient?: any };

// Exported (unconnected) for unit tests; the app consumes the connected default export below.
export class App extends React.Component<Props, IAppContainerState> {
  private completedSimulations: ISessionForList[] = [];
  private completedSimulationsFetched: boolean = false;
  private completedSimulationsFetching: boolean = false;
  private prevCompletedSimulationsFetched: boolean = false;

  public state: IAppContainerState = {
    showIntro: true,
    loaded: false,
  };

  public componentDidMount() {
    this.authenticationCheck();
  }

  private fetchProjectsForLearner = (prevProps: Props) => {
    // Only fetch projects for learner roles when they're not already loaded
    if (
      this.props.userRole &&
      _has(LEARNER_ROLES, this.props.userRole.id) &&
      this.props.clientId &&
      (!this.props.projects || this.props.projects.length === 0)
    ) {
      // Fetch if: role changed OR authentication completed (but not on every update)
      const roleChanged = !prevProps.userRole || prevProps.userRole.id !== this.props.userRole.id;
      const authCompleted =
        prevProps.status !== this.props.status && this.props.status === AuthStatus.AUTHENTICATION_SUCCESSFUL;

      if (roleChanged || authCompleted) {
        this.props.refreshClientsList(getCommonProjectConfig(), this.props.clientId);
        // Fetch completed simulations
        this.fetchCompletedSimulations();
      }
    }
  };

  private fetchCompletedSimulations = async () => {
    // Only fetch for learner roles
    if (!this.props.userRole || !_has(LEARNER_ROLES, this.props.userRole.id)) {
      return;
    }

    if (this.completedSimulationsFetching) {
      return; // Already fetching, don't fetch again
    }

    this.completedSimulationsFetching = true;
    try {
      const response = await this.props.fetchSessionListResponse(
        { size: 9999, page: 0, sortByOrder: "desc", asc: false },
        completedSessionsQuery
      );
      this.completedSimulations = response.content || [];
      this.completedSimulationsFetched = true;
      this.completedSimulationsFetching = false;
      // Force a re-render to trigger componentDidUpdate with the new data
      this.forceUpdate();
    } catch (error) {
      this.completedSimulations = [];
      this.completedSimulationsFetched = true;
      this.completedSimulationsFetching = false;
    }
  };

  public async componentDidUpdate(prevProps: Props) {
    // Initial auth token check has just finished
    // or Auth Error occurred during the loading process
    if (prevProps.status !== this.props.status && this.props.status === AuthStatus.NOT_AUTHENTICATED) {
      // User is not authenticated and should be redirected to the Login Page.
      // On a genuine logout/teardown (was AUTHENTICATION_SUCCESSFUL) also drop any in-memory SSO deep-link
      // target so a logout->login within this same mount can't resurrect the previous session's target — the
      // gatesPending deferral now keeps redirectUrl in state across the onboarding gates, so it would
      // otherwise outlive the session that set it. Scoped to the AUTHENTICATED transition — NOT the initial
      // CHECKING->NOT_AUTHENTICATED load — so a not-yet-logged-in deep-link arrival keeps its target for the
      // subsequent in-app login. See QAT-285.
      const cameFromAuthenticated = prevProps.status === AuthStatus.AUTHENTICATION_SUCCESSFUL;
      this.setState({
        loaded: true,
        authFailedPath: cameFromAuthenticated ? this.props.location : undefined,
        ...(cameFromAuthenticated ? { redirectUrl: undefined } : {}),
      });
    }

    if (prevProps.status !== this.props.status && this.props.status === AuthStatus.SWITCHING_ROLE) {
      this.setState({
        authFailedPath: undefined,
        loaded: false,
      });
    }

    if (
      prevProps.status === AuthStatus.AUTHENTICATION_SUCCESSFUL &&
      this.props.status === AuthStatus.NOT_AUTHENTICATED &&
      !this.isAssignPasswordLocation() &&
      !this.isPasswordRestoreLocation()
    ) {
      this.props.history.replace(ROUTE_PATHS.HOME, []);
    }

    if (prevProps.status !== this.props.status && this.props.status === AuthStatus.AUTHENTICATION_SUCCESSFUL) {
      this.setState({
        authFailedPath: undefined,
        loaded: false,
        showIntro: false,
      });

      if (this.props.wsConnectionStatus === WSConnectionStatus.WS_DISCONNECTED) {
        this.props.initWebSocketConnection();
      }
    }

    // User profile has been loaded
    if (
      !this.state.loaded &&
      this.props.status === AuthStatus.AUTHENTICATION_SUCCESSFUL &&
      (!prevProps.userRole || (this.props.userRole && prevProps.userRole.id !== this.props.userRole.id)) &&
      this.props.profileLoaded
    ) {
      this.setState({
        authFailedPath: undefined,
        loaded: true,
      });
    }

    // Fetch projects for learner roles when needed
    this.fetchProjectsForLearner(prevProps);

    // Check for virtual sim redirect when projects are loaded for learner role
    // Check if: projects just loaded OR completedSimulations just fetched
    const { userRole, projects, userProfile, location } = this.props;
    const projectsJustLoaded =
      projects && projects.length > 0 && (!prevProps.projects || prevProps.projects.length === 0);
    const completedSimulationsJustFetched = this.completedSimulationsFetched && !this.prevCompletedSimulationsFetched;

    const shouldCheckRedirect =
      !this.completedSimulationsFetching &&
      projects &&
      projects.length > 0 &&
      (projectsJustLoaded || completedSimulationsJustFetched);

    // Update the previous fetch state
    this.prevCompletedSimulationsFetched = this.completedSimulationsFetched;

    if (shouldCheckRedirect) {
      const completedSimulations = this.completedSimulationsFetched ? this.completedSimulations : undefined;
      if (
        checkVirtualSimRedirect({
          userRole,
          projects,
          prevProjects: prevProps.projects,
          userProfile,
          completedSimulations,
          location,
        })
      ) {
        return; // Redirect happened, don't continue with normal flow
      }
    }
  }

  public render() {
    const language = this.props.language;
    moment.locale(language);
    momentUpdateLocale(language);
    localStorage.setItem("locale", language);

    if (!this.state.loaded) {
      return <LoadingOverlay active={true} spinner={true} />;
    }

    return (
      // TODO: change background color for particular routs
      <ErrorBoundary>
        <Suspense fallback={<LoadingOverlay isFallback={true} active={true} spinner={true} />}>
          <Switch>
            <Route path={ROUTE_PATHS.ERROR} component={ErrorPageRoute} />
            <Route path={ROUTE_PATHS.SUCCESS} component={SuccessPageRoute} />
            <Route path={ROUTE_PATHS.EMAIL_VERIFICATION} component={EmailVerificationPage} />
            <Route>
              <ErrorModal />
              <RefreshSessionTimeoutModal />
              <div className={styles.appRoot} role={"application"}>
                {this.state.showIntro && <LoginLoader />}
                {this.renderRoutes()}
              </div>
              <CookiePopupContainer />
            </Route>
          </Switch>
        </Suspense>
      </ErrorBoundary>
    );
  }

  private isAssignPasswordLocation() {
    return this.props.location.pathname.match("/join");
  }

  // QAT-366: the forgot-password page must render even mid-sign-in (e.g. a user who reached the OTP step
  // clicks "reset your password" from the verification email). Like /join, force the unauthorized flow so
  // Root's TOTP gate does not swallow the route. The TOTP cookie is intentionally left intact so navigating
  // elsewhere still requires OTP verification.
  private isPasswordRestoreLocation() {
    return this.props.location.pathname === ROUTE_PATHS.PASSWORD_RESTORE;
  }

  private renderRoutes() {
    const { redirectUrl } = this.state;
    const { userProfile } = this.props;
    const { clientConfig, isUserOnboarded, companyConfig } = this.props;
    const externalLoginDataCookie = Cookies.get(PARAM_LOGIN_EXTERNAL);
    const isEmbedded = isEmbeddedIframe();

    if (externalLoginDataCookie) {
      return (
        <SsoCheck
          externalLoginData={JSON.parse(externalLoginDataCookie) as IExternalLoginData}
          redirectUrl={redirectUrl}
        />
      );
    }

    // If the app is embedded(iframe) and there is a redirectUrl, redirect to the redirectUrl
    if (isEmbedded && redirectUrl) {
      const decodedUrl = decodeURIComponent(redirectUrl);
      const host = window.location.host;

      if (
        decodedUrl.startsWith("http://") ||
        (decodedUrl.startsWith("https://") && host.endsWith(HOST) && !isDevelopment())
      ) {
        if (!userProfile?.eulaAccepted) {
          this.props.setScormRedirectUrl(decodedUrl);
        } else {
          window.location.replace(decodedUrl);
        }
      }
    }

    const loggedIn = this.props.status === AuthStatus.AUTHENTICATION_SUCCESSFUL;
    if (!loggedIn || this.isAssignPasswordLocation() || this.isPasswordRestoreLocation()) {
      const from = this.state.authFailedPath || this.props.location;

      return <UnauthorizedRoot from={from} />;
    }

    // Honor a cross-app SSO redirect target (?to=) for logged-in users. The email -> backend SSO flow
    // returns to /login?to=<cross-app url> without the external-login cookie, so SsoCheck never renders
    // and the target would otherwise be dropped (the learner lands on the default dashboard). Cross-app
    // paths (/on-demand, ...) need a full-page navigation so the gateway routes them to the owning app;
    // getCrossAppRedirect also guards against open redirects. Skipped when embedded (handled above) and
    // on localhost/dev where the target app isn't served.
    //
    // gatesPending defers the redirect while a new user still has to clear the EULA / fill-profile / (for
    // virtual-sim-only learners) registration gates. Those gates live in Root.render, which never mounts if
    // we short-circuit to the cross-app target here — so a brand-new user opening an SSO scenario deep link
    // skipped registration and landed straight on the on-demand pathway page (QAT-285). Falling through lets
    // Root run the gates; redirectUrl is kept in state, so once EULA + fill-profile complete in-SPA the next
    // render honors the target.
    const gatesPending = !userProfile?.eulaAccepted || !userProfile?.filled;
    const crossAppTarget = resolveSsoCrossAppTarget(redirectUrl, {
      loggedIn,
      isEmbedded,
      isDevelopment: isDevelopment(),
      gatesPending,
    });
    if (crossAppTarget) {
      window.location.replace(crossAppTarget);
      return <LoadingOverlay active={true} spinner={true} />;
    }

    const userRole = this.props.userRole;

    if (!userRole) {
      return null;
    }

    const routesConfig: any = getRoutesConfig(
      userRole.id,
      getRoutesPath(clientConfig, isUserOnboarded, companyConfig),
      companyConfig?.nextGenScenario || false
    );

    if (!routesConfig) {
      return null;
    }

    return (
      <UseRoleLink>
        {({ refreshing }: IUseRoleLinkParams) => !refreshing && <AuthorizedRoot routesConfig={routesConfig} />}
      </UseRoleLink>
    );
  }

  private async authenticationCheck() {
    const { location, getToken, history } = this.props;
    const access_token = getUrlParamValue("access_token", location); // tslint:disable-line
    const refresh_token = getUrlParamValue("refresh_token", location); // tslint:disable-line
    const refresh_token_exp_duration = +getUrlParamValue("refresh_token_exp_duration", location); // tslint:disable-line
    const redirectUrl = getUrlParamValue("to", location);
    const stateId = getUrlParamValue("state_id", location);

    if (stateId) {
      try {
        const tokenInfo = await getToken(stateId);
        this.props.saveToken({
          access_token: tokenInfo.accessToken,
          refresh_token: tokenInfo.refreshToken,
          refresh_token_exp_duration: tokenInfo.refreshTokenExpirationDuration,
        });
      } catch (e) {
        // do nothing
      }
    } else if (!!access_token && !!refresh_token) {
      // remove the tokens from url
      updateUrlParamsValue(
        {
          access_token: undefined,
          refresh_token: undefined,
        },
        location,
        history
      );

      this.props.saveToken({
        access_token,
        refresh_token,
        refresh_token_exp_duration,
      });
    }

    if (redirectUrl) {
      this.setState({
        redirectUrl,
      });
    }
    this.props.authenticationCheck();
  }
}
export default hot(withRouter(connector(App)));
